// Where the client runs: the web (served by the backend: same origin) or the
// native app (Capacitor: the UI is bundled in the app and talks to a server
// whose address the user can set on the login screen).
//
// In the app, a <base href="https://server/"> is inserted once the bundle has
// loaded, so every relative URL the client uses — API calls, uploads, media
// (/media/<id>), avatars — resolves to the server with no per-call changes.

type CapWindow = { Capacitor?: { isNativePlatform?: () => boolean; getPlatform?: () => string } };

function forced() {
  try {
    return localStorage.getItem("xmm.forceNative") === "1"; // tests: the app's code path in a browser
  } catch {
    return false;
  }
}
export const isNative = !!(window as unknown as CapWindow).Capacitor?.isNativePlatform?.() || forced();
function forcedPlatform() {
  try {
    return localStorage.getItem("xmm.forcePlatform"); // tests: "android" plays the phone app's part
  } catch {
    return null;
  }
}
export const nativePlatform = (window as unknown as CapWindow).Capacitor?.getPlatform?.() ?? forcedPlatform() ?? "web"; // android | ios | web
/** The phone app: the primary device (it logs in by SMS and links the others). */
export const onPhone = nativePlatform === "android" || nativePlatform === "ios";
/** Test browsers may log in by SMS too (a dev / test server allows it). */
export function testLogin() {
  try {
    return localStorage.getItem("xmm.testLogin") === "1";
  } catch {
    return false;
  }
}

const SERVER_KEY = "xmm.server";

/** The server's origin ("https://chat.example.com"); same-origin on the web. */
export function serverOrigin(): string {
  if (!isNative) return location.origin;
  let v: string | null = null;
  try {
    v = localStorage.getItem(SERVER_KEY);
  } catch {
    /* storage blocked */
  }
  return (v || import.meta.env.VITE_SERVER_URL || "http://10.0.2.2:8080").replace(/\/+$/, "");
}

export function setServerOrigin(url: string) {
  const u = new URL(url.trim());
  localStorage.setItem(SERVER_KEY, u.origin);
}

const TUNNEL_KEY = "xmm.tunnelKey";

/** Key the dev tunnel (scripts/share.sh) accepts instead of its password. */
export function tunnelKey(): string {
  try {
    return localStorage.getItem(TUNNEL_KEY) || import.meta.env.VITE_TUNNEL_KEY || "";
  } catch {
    return import.meta.env.VITE_TUNNEL_KEY || "";
  }
}

export function setTunnelKey(k: string) {
  localStorage.setItem(TUNNEL_KEY, k.trim());
}

/** App -> server requests carry the tunnel key and skip ngrok's browser
 * warning page (fetch and XHR; media and the WebSocket need neither). */
function addTunnelHeaders() {
  const key = tunnelKey();
  const server = serverOrigin();
  const extra: Record<string, string> = { "ngrok-skip-browser-warning": "1" };
  if (key) extra["X-Xmm-Tunnel"] = key;
  const toServer = (u: string) => {
    try {
      return new URL(u, document.baseURI).origin === server;
    } catch {
      return false;
    }
  };
  const origFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    const url = input instanceof Request ? input.url : String(input);
    if (!toServer(url)) return origFetch(input, init);
    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    for (const [k, v] of Object.entries(extra)) headers.set(k, v);
    return origFetch(input, { ...init, headers });
  };
  const open = XMLHttpRequest.prototype.open;
  const send = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (this: XMLHttpRequest & { _xmmServer?: boolean }, method: string, url: string | URL, ...rest: unknown[]) {
    this._xmmServer = toServer(String(url));
    return (open as (...a: unknown[]) => void).call(this, method, url, ...rest);
  } as typeof XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.send = function (this: XMLHttpRequest & { _xmmServer?: boolean }, body?: Document | XMLHttpRequestBodyInit | null) {
    if (this._xmmServer) for (const [k, v] of Object.entries(extra)) this.setRequestHeader(k, v);
    return send.call(this, body);
  };
}

/** Call before rendering: point relative URLs at the server (native only). */
export function installServerBase() {
  if (!isNative) return;
  addTunnelHeaders();
  let base = document.querySelector("base");
  if (!base) {
    base = document.createElement("base");
    document.head.prepend(base);
  }
  base.href = serverOrigin() + "/";
}

/** Base for resolving/validating URLs the server hands us. */
export const baseURL = () => document.baseURI;
export const baseOrigin = () => new URL(document.baseURI).origin;

/** Clear ?query / path from the address bar (a no-op concept in the app). */
export function cleanAddressBar() {
  if (isNative) return;
  history.replaceState(null, "", location.origin + "/");
}
